import type { AuthedRequester } from "@/lib/brokerAuth";

// BRK-S425 — the submission's message thread with Signal surety:
// the Messages tab reads it and the tab's composer and the header's Message
// Underwriter dialog post to it.
//
// BOTH ROUTES ARE PMS-S842's, pinned at BRK-S511 — seam surfaces, with the
// post's body held to the pinned request in
// `test/lib/submissionMessagesContract.test.ts`. A sent message is shown only once a re-read of the thread
// carries it (NN #7): nothing is appended on the client.

const SUBMISSIONS = "/api/broker/bond/submissions";

/** The bounds the composer holds a message to (PMS's message schema,
 *  PMS-S356, shared with the account's "Message us"). */
export { MESSAGE_BODY_MAX } from "@/lib/accountMessage";

/** What a message is about. CLOSED, in the wireframe's order; the first is
 *  the default. PMS-S842 (P7) — `topic`. */
export const MESSAGE_TOPICS = [
  "general_question",
  "bond_terms",
  "documents",
  "bid_result",
  "timing",
] as const;
export type MessageTopic = (typeof MESSAGE_TOPICS)[number];

export const DEFAULT_MESSAGE_TOPIC: MessageTopic = "general_question";

const TOPIC_LABELS: Record<MessageTopic, string> = {
  general_question: "General question",
  bond_terms: "Bond terms",
  documents: "Documents",
  bid_result: "Bid result",
  timing: "Timing",
};

export function isMessageTopic(v: unknown): v is MessageTopic {
  return MESSAGE_TOPICS.some((t) => t === v);
}

/** A topic in words; one this build has not adopted reads "Other". */
export function messageTopicLabel(raw: unknown): string {
  return isMessageTopic(raw) ? TOPIC_LABELS[raw] : "Other";
}

/** Who wrote a message. PMS-S842 (P7) — `from`. */
export const MESSAGE_SIDES = ["broker", "signal"] as const;
export type MessageSide = (typeof MESSAGE_SIDES)[number];

/** One message as PMS serves it. */
export interface SubmissionMessageWire {
  /** PMS-S842 (P7) — `id`. */
  id: string;
  /** PMS-S842 (P7) — `at`, ISO. */
  at: string;
  /** PMS-S842 (P7) — `from`. */
  from: MessageSide;
  /** PMS-S842 (P7) — `author`, PMS's broker-safe display
   *  name; the only name the thread ever shows. */
  author: string | null;
  /** PMS-S842 (P7) — `topic`. */
  topic: MessageTopic;
  /** PMS-S842 (P7) — `body`, plain text. */
  body: string;
}

/** A message as the thread renders it: an unknown side or topic is null. */
export interface SubmissionMessage {
  id: string;
  at: string;
  from: MessageSide | null;
  author: string | null;
  topic: MessageTopic | null;
  body: string;
}

export interface SubmissionMessageInput {
  topic: MessageTopic;
  body: string;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const filledText = (v: unknown): v is string =>
  typeof v === "string" && v.trim() !== "";

/**
 * The thread, oldest first. A row without an id, a time or a body is dropped;
 * a side or topic this build does not know reads as null (a neutral label),
 * never a dropped message. A body that is not a list is NULL — an unreadable
 * thread is not an empty one.
 */
export function readSubmissionMessages(
  raw: unknown,
): SubmissionMessage[] | null {
  if (!Array.isArray(raw)) return null;
  const read: SubmissionMessage[] = [];
  for (const m of raw as unknown[]) {
    if (!isRecord(m)) continue;
    if (!filledText(m.id) || !filledText(m.at) || !filledText(m.body)) {
      continue;
    }
    read.push({
      id: m.id,
      at: m.at,
      from: MESSAGE_SIDES.find((s) => s === m.from) ?? null,
      author: filledText(m.author) ? m.author.trim() : null,
      topic: isMessageTopic(m.topic) ? m.topic : null,
      body: m.body,
    });
  }
  // ISO timestamps sort as text; oldest first.
  return read.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
}

/**
 * The name line. The broker's side is "You" (the brokerage's own messages);
 * Signal's is "Signal", with PMS's served `author` beside it —
 * no other name is ever shown.
 */
export function messageAuthorLabel(
  m: Pick<SubmissionMessage, "from" | "author">,
): string {
  if (m.from === "broker") return "You";
  if (m.from === "signal") {
    return m.author ? `Signal · ${m.author}` : "Signal";
  }
  return m.author ?? "Unknown sender";
}

const threadPath = (submissionId: string) =>
  `${SUBMISSIONS}/${encodeURIComponent(submissionId)}/messages`;

/** GET /api/broker/bond/submissions/{id}/messages — PMS-S842.
 *  Throws on a body it cannot read, so the tab says the read failed. */
export async function listSubmissionMessages(
  request: AuthedRequester,
  submissionId: string,
): Promise<SubmissionMessage[]> {
  const thread = readSubmissionMessages(
    await request<unknown>(threadPath(submissionId)),
  );
  if (thread === null) throw new Error("Unreadable message thread.");
  return thread;
}

/** POST /api/broker/bond/submissions/{id}/messages (PMS-S842). PMS answers with the message it filed. */
export function sendSubmissionMessage(
  request: AuthedRequester,
  submissionId: string,
  input: SubmissionMessageInput,
): Promise<SubmissionMessageWire> {
  return request<SubmissionMessageWire>(threadPath(submissionId), {
    method: "POST",
    body: input,
  });
}
